import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/api/schema';
import { useBranches } from '@/hooks/api/branches';
import { useProfiles } from '@/hooks/api/profiles';
import { useMerchantId } from '@/hooks/useMerchantId';
import { useSalesWithItems } from '@/hooks/use-sales-with-items';
import { formatUZS } from '@/lib/format';
import {
  BarChart3, TrendingUp, CreditCard, Wallet, Users, Building2,
  ShoppingCart, PieChart as PieIcon, ArrowUpRight, ArrowDownRight, UserCheck, Undo2, Package,
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import {
  resolvePeriod, filterSales, sumSales, pctChange, refundedSummary, slowMovingProducts,
  type PeriodKey, type SaleLike,
} from '@/lib/report-metrics';
import { ReportFilters } from '@/components/reports/ReportFilters';
import { SalesTrendChart } from '@/components/reports/SalesTrendChart';
import { ActivityCharts } from '@/components/reports/ActivityCharts';
import { TopProducts } from '@/components/reports/TopProducts';
import { CategoryBreakdown } from '@/components/reports/CategoryBreakdown';
import { PaymentPieChart, type PaymentSlice } from '@/components/reports/PaymentPieChart';
import { ExportButtons } from '@/components/reports/ExportButtons';
import { ChartTooltipBox, compactUZS } from '@/components/reports/chart-bits';
import { format } from 'date-fns';
import { FETCH_ALL_MAX_PAGES, FETCH_ALL_PER_PAGE } from '@/lib/fetch-all';

type MurabahaCase = components['schemas']['MurabahaCaseResource'];
type Customer = components['schemas']['CustomerResource'];
type Product = components['schemas']['ProductResource'];


export default function Reports() {
  const { t } = useTranslation();
  const { merchantId } = useMerchantId();
  const [period, setPeriod] = useState<PeriodKey>('30days');
  const [customFrom, setCustomFrom] = useState(format(new Date(), 'yyyy-MM-01'));
  const [customTo, setCustomTo] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [branchId, setBranchId] = useState('__all__');

  const { range, previous } = useMemo(
    () => resolvePeriod(period, new Date(), {
      from: customFrom ? new Date(customFrom) : undefined,
      to: customTo ? new Date(customTo) : undefined,
    }),
    [period, customFrom, customTo],
  );

  const trendFrom = useMemo(() => {
    const earliest = previous ? previous.from : range.from;
    const yearBack = new Date();
    yearBack.setFullYear(yearBack.getFullYear() - 1);
    return earliest.getTime() < yearBack.getTime() ? earliest : yearBack;
  }, [range, previous]);

  const { data: allSales = [], isLoading: salesLoading } = useSalesWithItems(trendFrom);
  const { data: murabahaCases = [], isLoading: murabahaLoading } = useQuery({
    queryKey: ['/murabaha/cases', merchantId],
    queryFn: async () => {
      const all: MurabahaCase[] = [];
      for (let page = 1; page <= FETCH_ALL_MAX_PAGES; page += 1) {
        const { data, error } = await apiClient.GET('/murabaha/cases', { params: { query: { page, per_page: FETCH_ALL_PER_PAGE } as never } });
        if (error) throw error;
        all.push(...(data?.data ?? []));
        if (page >= (data?.meta?.last_page ?? 1)) break;
      }
      return all;
    },
    enabled: !!merchantId,
  });
  const { data: branches = [] } = useBranches();
  const { data: profiles = [] } = useProfiles();
  const { data: customersData } = useQuery({ queryKey: ['/customers'], enabled: !!merchantId });
  const customers = (customersData?.data ?? []) as Customer[];

  const { data: productsData } = useQuery({ queryKey: ['/products'], enabled: !!merchantId });
  const products = (productsData?.data ?? []) as Product[];

  const isLoading = salesLoading || murabahaLoading;
  const activeBranch = branchId === '__all__' ? null : branchId;

  const scopedSales = useMemo<SaleLike[]>(
    () => filterSales(allSales, { branchId: activeBranch }),
    [allSales, activeBranch],
  );
  const periodSales = useMemo(
    () => filterSales(allSales, { range, branchId: activeBranch }),
    [allSales, range, activeBranch],
  );
  const prevSales = useMemo(
    () => (previous ? filterSales(allSales, { range: previous, branchId: activeBranch }) : []),
    [allSales, previous, activeBranch],
  );

  const totalSales = sumSales(periodSales);
  const totalCount = periodSales.length;
  const avgCheck = totalCount > 0 ? totalSales / totalCount : 0;
  const prevTotal = sumSales(prevSales);
  const prevCount = prevSales.length;
  const prevAvg = prevCount > 0 ? prevTotal / prevCount : 0;
  const murabahaSalesList = periodSales.filter((s) => s.is_murabaha);
  const murabahaSales = sumSales(murabahaSalesList);
  const prevMurabaha = sumSales(prevSales.filter((s) => s.is_murabaha));
  const refunds = refundedSummary(allSales, { range, branchId: activeBranch });

  // === MURABAHA ===
  const filteredMurabaha = useMemo(
    () => murabahaCases.filter((m: any) => {
      if (activeBranch && String(m.branch_id) !== activeBranch) return false;
      const t = new Date(m.created_at).getTime();
      return t >= range.from.getTime() && t <= range.to.getTime();
    }),
    [murabahaCases, range, activeBranch],
  );
  const murabahaTotal = filteredMurabaha.reduce((s: number, m: any) => s + Number(m.total_amount), 0);
  const murabahaPending = filteredMurabaha.filter((m: any) => ['Draft', 'PendingCustomerConfirmation'].includes(m.status));
  const murabahaCompleted = filteredMurabaha.filter((m: any) => m.status === 'Completed');
  const groupBy = (key: string) =>
    Object.entries(
      filteredMurabaha.reduce((acc: Record<string, { count: number; sum: number }>, m: any) => {
        acc[m[key]] = acc[m[key]] || { count: 0, sum: 0 };
        acc[m[key]].count++;
        acc[m[key]].sum += Number(m.total_amount);
        return acc;
      }, {}),
    );
  const murabahaByStatus = groupBy('status');
  const murabahaByPayment = groupBy('payment_status');
  const murabahaStatusChart = murabahaByStatus.map(([status, d]) => ({
    label: t(`tick_reports.murabahaStatus.${status}`, { defaultValue: status }),
    sum: (d as any).sum,
    count: (d as any).count,
  }));

  // === FILIALLAR ===
  const salesByBranch = Object.entries(
    periodSales.reduce((acc: Record<string, { count: number; sum: number }>, s: any) => {
      acc[s.branch_id] = acc[s.branch_id] || { count: 0, sum: 0 };
      acc[s.branch_id].count++;
      acc[s.branch_id].sum += Number(s.total_amount);
      return acc;
    }, {}),
  ).map(([id, data]) => ({
    branch: branches.find((b: any) => String(b.id) === id),
    ...(data as { count: number; sum: number }),
  })).sort((a, b) => b.sum - a.sum);

  // === XODIMLAR ===
  const salesByCashier = Object.entries(
    periodSales.reduce((acc: Record<string, { count: number; sum: number }>, s: any) => {
      const cid = s.cashier_id || 'unknown';
      acc[cid] = acc[cid] || { count: 0, sum: 0 };
      acc[cid].count++;
      acc[cid].sum += Number(s.total_amount);
      return acc;
    }, {}),
  ).map(([cashierId, data]) => ({
    profile: profiles.find((p: any) => String(p.id) === cashierId),
    cashierId,
    ...(data as { count: number; sum: number }),
  })).sort((a, b) => b.sum - a.sum);

  // === TO'LOV USULLARI ===
  const paymentMethods = [
    { key: 'cash', label: t('tick_reports.methodCash'), icon: Wallet, color: 'hsl(var(--success))', tint: 'text-success' },
    { key: 'card', label: t('tick_reports.methodCard'), icon: CreditCard, color: 'hsl(var(--info))', tint: 'text-info' },
    { key: 'hisob_wallet', label: t('tick_reports.methodWallet'), icon: Wallet, color: 'hsl(var(--warning))', tint: 'text-warning' },
    { key: 'murabaha', label: t('tick_reports.methodMurabaha'), icon: ShoppingCart, color: 'hsl(var(--primary))', tint: 'text-primary' },
  ];
  const paymentBreakdown: PaymentSlice[] = paymentMethods.map((pm) => {
    const list = pm.key === 'murabaha'
      ? periodSales.filter((s) => s.is_murabaha)
      : periodSales.filter((s) => s.payment_method === pm.key && !s.is_murabaha);
    const sum = sumSales(list);
    return { ...pm, count: list.length, sum, pct: totalSales > 0 ? (sum / totalSales) * 100 : 0 };
  });

  const topCustomers = [...customers]
    .sort((a: any, b: any) => Number(b.total_spent) - Number(a.total_spent))
    .slice(0, 10);

  const slowProducts = slowMovingProducts(periodSales, products as any[]);

  const Delta = ({ current, prev }: { current: number; prev: number }) => {
    const pct = previous ? pctChange(current, prev) : null;
    if (pct === null) return null;
    const up = pct >= 0;
    return (
      <span className={`inline-flex items-center gap-0.5 text-xs ${up ? 'text-success' : 'text-destructive'}`}>
        {up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
        {Math.abs(pct).toFixed(1)}%
      </span>
    );
  };

  const StatCard = ({ icon: Icon, label, value, sub, color = 'text-primary', delta }: any) => (
    <div className="bg-card border border-border rounded-xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} className={color} />
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <p className="text-lg font-semibold text-foreground">{isLoading ? '...' : value}</p>
      <div className="flex items-center gap-2 mt-0.5">
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
        {delta}
      </div>
    </div>
  );

  return (
    <div className="p-6 max-w-6xl mx-auto animate-fade-in">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-foreground">{t('tick_reports.title')}</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {period === 'all'
              ? t('tick_reports.allPeriod')
              : `${format(range.from, 'dd.MM.yyyy')} — ${format(range.to, 'dd.MM.yyyy')}`}
            {previous && ` ${t('tick_reports.comparedPrev')}`}
          </p>
        </div>
        <ReportFilters
          period={period}
          onPeriodChange={setPeriod}
          customFrom={customFrom}
          customTo={customTo}
          onCustomFromChange={setCustomFrom}
          onCustomToChange={setCustomTo}
          branchId={branchId}
          onBranchChange={setBranchId}
          branches={branches as any[]}
        />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <StatCard
          icon={BarChart3}
          label={t('tick_reports.totalSales')}
          value={formatUZS(totalSales)}
          sub={t('tick_reports.count', { n: totalCount })}
          delta={<Delta current={totalSales} prev={prevTotal} />}
        />
        <StatCard
          icon={TrendingUp}
          label={t('tick_reports.avgCheck')}
          value={formatUZS(avgCheck)}
          color="text-success"
          delta={<Delta current={avgCheck} prev={prevAvg} />}
        />
        <StatCard
          icon={ShoppingCart}
          label={t('tick_reports.murabahaSales')}
          value={formatUZS(murabahaSales)}
          sub={t('tick_reports.count', { n: murabahaSalesList.length })}
          color="text-warning"
          delta={<Delta current={murabahaSales} prev={prevMurabaha} />}
        />
        <StatCard
          icon={Undo2}
          label={t('tick_reports.refunded')}
          value={formatUZS(refunds.sum)}
          sub={t('tick_reports.count', { n: refunds.count })}
          color="text-destructive"
        />
        <StatCard icon={Users} label={t('tick_reports.customers')} value={t('tick_reports.count', { n: customers.length })} color="text-info" />
      </div>

      <Tabs defaultValue="trend" className="space-y-4">
        <TabsList className="bg-muted/50 p-1 flex-wrap h-auto">
          <TabsTrigger value="trend">{t('tick_reports.tabTrend')}</TabsTrigger>
          <TabsTrigger value="products">{t('tick_reports.tabProducts')}</TabsTrigger>
          <TabsTrigger value="payment">{t('tick_reports.tabPayment')}</TabsTrigger>
          <TabsTrigger value="murabaha">{t('tick_reports.methodMurabaha')}</TabsTrigger>
          <TabsTrigger value="branches">{t('tick_reports.tabBranches')}</TabsTrigger>
          <TabsTrigger value="employees">{t('tick_reports.tabEmployees')}</TabsTrigger>
          <TabsTrigger value="customers">{t('tick_reports.tabTopCustomers')}</TabsTrigger>
        </TabsList>

        {/* DINAMIKA */}
        <TabsContent value="trend" className="space-y-4">
          <SalesTrendChart sales={scopedSales} />
          <ActivityCharts sales={periodSales} />
        </TabsContent>

        {/* TOVARLAR */}
        <TabsContent value="products" className="space-y-4">
          <TopProducts sales={periodSales} isLoading={salesLoading} />
          <CategoryBreakdown sales={periodSales} />
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="p-4 border-b border-border">
              <h3 className="text-sm font-medium text-foreground">{t('tick_reports.slowTitle')}</h3>
              <p className="text-xs text-muted-foreground mt-0.5">{t('tick_reports.slowHint')}</p>
            </div>
            <div className="divide-y divide-border">
              {slowProducts.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">{t('tick_reports.allSold')}</div>
              ) : slowProducts.map((p: any) => (
                <div key={p.id} className="flex items-center justify-between p-3">
                  <div className="flex items-center gap-2">
                    <Package size={14} className="text-muted-foreground" />
                    <span className="text-sm text-foreground">{p.name}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{p.sku || '—'}</span>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* TO'LOV USULLARI */}
        <TabsContent value="payment">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-card border border-border rounded-xl p-4">
              <h2 className="text-sm font-medium text-foreground mb-2">{t('tick_reports.paymentShare')}</h2>
              <PaymentPieChart data={paymentBreakdown} />
            </div>
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="p-4 border-b border-border flex items-center justify-between gap-2">
                <h2 className="text-sm font-medium text-foreground">{t('tick_reports.distribution')}</h2>
                <ExportButtons
                  fileName="tolov-usullari"
                  title={t('tick_reports.tabPayment')}
                  columns={[
                    { header: t('tick_reports.colMethod'), key: 'label' },
                    { header: t('tick_reports.colTransactions'), key: 'count' },
                    { header: t('tick_reports.colSum'), key: 'sum' },
                    { header: t('tick_reports.colShare'), key: 'pct' },
                  ]}
                  rows={paymentBreakdown.map((pm) => ({
                    label: pm.label,
                    count: pm.count,
                    sum: formatUZS(pm.sum),
                    pct: `${pm.pct.toFixed(1)}%`,
                  }))}
                />
              </div>
              <div className="divide-y divide-border">
                {paymentMethods.map((pm, i) => {
                  const row = paymentBreakdown[i];
                  return (
                    <div key={pm.key} className="flex items-center justify-between p-4">
                      <div className="flex items-center gap-3">
                        <pm.icon size={18} className={pm.tint} />
                        <div>
                          <p className="text-sm font-medium text-foreground">{pm.label}</p>
                          <p className="text-xs text-muted-foreground">{t('tick_reports.transactionsCount', { n: row.count })}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-foreground">{formatUZS(row.sum)}</p>
                        <span className="text-xs text-muted-foreground">{row.pct.toFixed(1)}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* MURABAHA */}
        <TabsContent value="murabaha" className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={ShoppingCart} label={t('tick_reports.totalApps')} value={t('tick_reports.count', { n: filteredMurabaha.length })} sub={formatUZS(murabahaTotal)} color="text-warning" />
            <StatCard icon={TrendingUp} label={t('tick_reports.pending')} value={t('tick_reports.count', { n: murabahaPending.length })} sub={formatUZS(murabahaPending.reduce((s: number, m: any) => s + Number(m.total_amount), 0))} color="text-info" />
            <StatCard icon={BarChart3} label={t('tick_reports.completed')} value={t('tick_reports.count', { n: murabahaCompleted.length })} sub={formatUZS(murabahaCompleted.reduce((s: number, m: any) => s + Number(m.total_amount), 0))} color="text-success" />
            <StatCard icon={PieIcon} label={t('tick_reports.conversion')} value={filteredMurabaha.length > 0 ? `${(murabahaCompleted.length / filteredMurabaha.length * 100).toFixed(0)}%` : '0%'} color="text-primary" />
          </div>

          <div className="bg-card border border-border rounded-xl p-4">
            <h3 className="text-sm font-medium text-foreground mb-3">{t('tick_reports.sumByStatus')}</h3>
            {murabahaStatusChart.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">{t('tick_reports.noData')}</div>
            ) : (
              <div className="h-[240px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={murabahaStatusChart} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={compactUZS} width={56} />
                    <Tooltip
                      cursor={{ fill: 'hsl(var(--muted))', opacity: 0.4 }}
                      content={({ active, payload, label }) =>
                        active && payload?.length ? (
                          <ChartTooltipBox
                            title={String(label)}
                            rows={[
                              { label: t('tick_reports.colSum'), value: formatUZS(Number(payload[0]?.payload?.sum ?? 0)) },
                              { label: t('tick_reports.apps'), value: t('tick_reports.count', { n: payload[0]?.payload?.count ?? 0 }) },
                            ]}
                          />
                        ) : null
                      }
                    />
                    <Bar dataKey="sum" fill="hsl(var(--warning))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="p-4 border-b border-border">
              <h3 className="text-sm font-medium text-foreground">{t('tick_reports.byPaymentStatus')}</h3>
            </div>
            <div className="divide-y divide-border">
              {murabahaByPayment.length === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground">{t('tick_reports.noData')}</div>
              ) : murabahaByPayment.map(([status, data]) => (
                <div key={status} className="flex items-center justify-between p-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-foreground">{t(`tick_reports.paymentStatus.${status}`, { defaultValue: status })}</span>
                    <Badge variant="secondary" className="text-xs">{(data as any).count}</Badge>
                  </div>
                  <span className="text-sm font-medium text-foreground">{formatUZS((data as any).sum)}</span>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* FILIALLAR */}
        <TabsContent value="branches" className="space-y-4">
          <div className="bg-card border border-border rounded-xl p-4">
            <h2 className="text-sm font-medium text-foreground mb-3">{t('tick_reports.salesByBranch')}</h2>
            {salesByBranch.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">{t('tick_reports.noData')}</div>
            ) : (
              <div className="h-[240px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={salesByBranch.map((b) => ({ label: b.branch?.name || t('tick_reports.unknown'), sum: b.sum, count: b.count }))}
                    margin={{ top: 4, right: 8, bottom: 0, left: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={compactUZS} width={56} />
                    <Tooltip
                      cursor={{ fill: 'hsl(var(--muted))', opacity: 0.4 }}
                      content={({ active, payload, label }) =>
                        active && payload?.length ? (
                          <ChartTooltipBox
                            title={String(label)}
                            rows={[
                              { label: t('tick_reports.sale'), value: formatUZS(Number(payload[0]?.payload?.sum ?? 0)) },
                              { label: t('tick_reports.colTransactions'), value: t('tick_reports.count', { n: payload[0]?.payload?.count ?? 0 }) },
                            ]}
                          />
                        ) : null
                      }
                    />
                    <Bar dataKey="sum" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="p-4 border-b border-border flex items-center justify-between gap-2">
              <h2 className="text-sm font-medium text-foreground">{t('tick_reports.branchesTable')}</h2>
              <ExportButtons
                fileName="filiallar-savdo"
                title={t('tick_reports.salesByBranch')}
                columns={[
                  { header: t('tick_reports.colBranch'), key: 'branch' },
                  { header: t('tick_reports.colSales'), key: 'count' },
                  { header: t('tick_reports.colSum'), key: 'sum' },
                  { header: t('tick_reports.colShare'), key: 'pct' },
                ]}
                rows={salesByBranch.map((item) => ({
                  branch: item.branch?.name || t('tick_reports.unknown'),
                  count: item.count,
                  sum: formatUZS(item.sum),
                  pct: `${totalSales > 0 ? (item.sum / totalSales * 100).toFixed(1) : 0}%`,
                }))}
              />
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('tick_reports.colBranch')}</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('tick_reports.colSalesCount')}</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('tick_reports.colSum')}</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('tick_reports.colShare')}</th>
                </tr>
              </thead>
              <tbody>
                {salesByBranch.length === 0 ? (
                  <tr><td colSpan={4} className="p-6 text-center text-sm text-muted-foreground">{t('tick_reports.noData')}</td></tr>
                ) : salesByBranch.map((item, i) => (
                  <tr key={i} className="border-b border-border last:border-0 hover:bg-muted/30">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <Building2 size={14} className="text-muted-foreground" />
                        <span className="text-sm text-foreground">{item.branch?.name || t('tick_reports.unknown')}</span>
                      </div>
                    </td>
                    <td className="p-3 text-sm text-foreground">{t('tick_reports.count', { n: item.count })}</td>
                    <td className="p-3 text-sm font-medium text-foreground">{formatUZS(item.sum)}</td>
                    <td className="p-3 text-sm text-muted-foreground">
                      {totalSales > 0 ? (item.sum / totalSales * 100).toFixed(1) : 0}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* XODIMLAR */}
        <TabsContent value="employees">
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="p-4 border-b border-border flex items-center justify-between gap-2">
              <h2 className="text-sm font-medium text-foreground">{t('tick_reports.salesByEmployee')}</h2>
              <ExportButtons
                fileName="xodimlar-savdo"
                title={t('tick_reports.salesByEmployee')}
                columns={[
                  { header: t('tick_reports.colEmployee'), key: 'name' },
                  { header: t('tick_reports.colSales'), key: 'count' },
                  { header: t('tick_reports.colSum'), key: 'sum' },
                  { header: t('tick_reports.avgCheck'), key: 'avg' },
                ]}
                rows={salesByCashier.map((item) => ({
                  name: item.profile?.full_name || t('tick_reports.unknown'),
                  count: item.count,
                  sum: formatUZS(item.sum),
                  avg: formatUZS(item.count > 0 ? item.sum / item.count : 0),
                }))}
              />
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">#</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('tick_reports.colEmployee')}</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('tick_reports.colSales')}</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('tick_reports.colSum')}</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('tick_reports.avgCheck')}</th>
                </tr>
              </thead>
              <tbody>
                {salesByCashier.length === 0 ? (
                  <tr><td colSpan={5} className="p-6 text-center text-sm text-muted-foreground">{t('tick_reports.noData')}</td></tr>
                ) : salesByCashier.map((item, i) => (
                  <tr key={i} className="border-b border-border last:border-0 hover:bg-muted/30">
                    <td className="p-3 text-sm text-muted-foreground">{i + 1}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <UserCheck size={14} className="text-muted-foreground" />
                        <span className="text-sm text-foreground">{item.profile?.full_name || t('tick_reports.unknown')}</span>
                      </div>
                    </td>
                    <td className="p-3 text-sm text-foreground">{t('tick_reports.count', { n: item.count })}</td>
                    <td className="p-3 text-sm font-medium text-foreground">{formatUZS(item.sum)}</td>
                    <td className="p-3 text-sm text-muted-foreground">{formatUZS(item.count > 0 ? item.sum / item.count : 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* TOP MIJOZLAR */}
        <TabsContent value="customers">
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="p-4 border-b border-border flex items-center justify-between gap-2">
              <h2 className="text-sm font-medium text-foreground">{t('tick_reports.top10Customers')}</h2>
              <ExportButtons
                fileName="top-mijozlar"
                title={t('tick_reports.top10Customers')}
                columns={[
                  { header: t('tick_reports.colCustomer'), key: 'name' },
                  { header: t('tick_reports.phone'), key: 'phone' },
                  { header: t('tick_reports.purchases'), key: 'purchases' },
                  { header: t('tick_reports.spent'), key: 'spent' },
                ]}
                rows={topCustomers.map((c: any) => ({
                  name: c.full_name,
                  phone: c.phone,
                  purchases: c.total_purchases,
                  spent: formatUZS(Number(c.total_spent)),
                }))}
              />
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">#</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('tick_reports.colCustomer')}</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('tick_reports.phone')}</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('tick_reports.purchases')}</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('tick_reports.spent')}</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('tick_reports.murabahaLimit')}</th>
                </tr>
              </thead>
              <tbody>
                {topCustomers.length === 0 ? (
                  <tr><td colSpan={6} className="p-6 text-center text-sm text-muted-foreground">{t('tick_reports.noData')}</td></tr>
                ) : topCustomers.map((c: any, i: number) => (
                  <tr key={c.id} className="border-b border-border last:border-0 hover:bg-muted/30">
                    <td className="p-3 text-sm text-muted-foreground">{i + 1}</td>
                    <td className="p-3 text-sm text-foreground font-medium">{c.full_name}</td>
                    <td className="p-3 text-sm text-muted-foreground">{c.phone}</td>
                    <td className="p-3 text-sm text-foreground">{t('tick_reports.count', { n: c.total_purchases })}</td>
                    <td className="p-3 text-sm font-medium text-foreground">{formatUZS(Number(c.total_spent))}</td>
                    <td className="p-3 text-sm text-muted-foreground">{c.murabaha_limit ? formatUZS(Number(c.murabaha_limit)) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
